import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, type PropsWithChildren } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AUTH_SESSION_ENDED_EVENT, ApiError, clearStoredToken, getStoredToken } from "../api/client";
import { Button } from "../components/Button/Button";
import { InlineNotice } from "../components/Feedback/InlineNotice";
import { Spinner } from "../components/Feedback/Spinner";
import { loadSessionContext } from "./session";
import { SessionContextValue } from "./sessionContext";
import styles from "./SessionBoundary.module.css";

function PreviewGate({ children }: PropsWithChildren) {
  return (
    <main className={styles.gate}>
      <section className={styles.card}>{children}</section>
    </main>
  );
}

export function SessionBoundary({ children }: PropsWithChildren) {
  const token = getStoredToken();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTenantId = new URLSearchParams(location.search).get("tenant_id")?.trim() || null;
  const query = useQuery({
    queryKey: ["session-context", token, selectedTenantId],
    queryFn: () => loadSessionContext(selectedTenantId),
    enabled: Boolean(token),
    retry: (failureCount, error) => !(error instanceof ApiError && error.status < 500) && failureCount < 1,
  });
  const sessionEnded = query.isError && query.error instanceof ApiError && query.error.status === 401;
  const returnToSignIn = useCallback(() => {
    clearStoredToken();
    navigate("/login", {
      replace: true,
      state: {
        from: `${location.pathname}${location.search}`,
        notice: "Your session ended. Sign in again to continue.",
      },
    });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    window.addEventListener(AUTH_SESSION_ENDED_EVENT, returnToSignIn);
    return () => window.removeEventListener(AUTH_SESSION_ENDED_EVENT, returnToSignIn);
  }, [returnToSignIn]);

  useEffect(() => {
    if (sessionEnded) returnToSignIn();
  }, [returnToSignIn, sessionEnded]);

  if (sessionEnded) {
    return (
      <PreviewGate>
        <div className={styles.loading} role="status">
          <Spinner size="large" />
          <div>
            <strong>Returning you to sign in</strong>
            <span>Your previous session has ended.</span>
          </div>
        </div>
      </PreviewGate>
    );
  }

  if (!token) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (query.isPending) {
    return (
      <PreviewGate>
        <div className={styles.loading} role="status">
          <Spinner size="large" />
          <div>
            <strong>Loading your workspace</strong>
            <span>Applying the current tenant and permission context.</span>
          </div>
        </div>
      </PreviewGate>
    );
  }

  if (query.isError) {
    const requestId = query.error instanceof ApiError ? query.error.payload.request_id : undefined;
    return (
      <PreviewGate>
        <InlineNotice tone="error" title="Unable to load your workspace">
          {query.error.message}{requestId ? ` Request ID: ${requestId}` : ""}
        </InlineNotice>
        <div className={styles.actions}>
          <Button onClick={() => void query.refetch()}>Try again</Button>
          <Button
            onClick={() => {
              clearStoredToken();
              navigate("/login", { replace: true, state: { notice: "Your session ended. Sign in again to continue." } });
            }}
          >
            Sign in again
          </Button>
        </div>
      </PreviewGate>
    );
  }

  if (query.data.user.must_change_password) {
    return (
      <Navigate
        to="/change-password"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <SessionContextValue.Provider value={query.data}>{children}</SessionContextValue.Provider>;
}
